<template>
  <div class="opc-card-cascade">
    <template v-if="billingCycleSwitchVisible">
      <div class="opc-card-cascade__item">
        <div id="group0-title" class="opc-card-cascade__title">
          <slot :name="group0.slot">
            {{ group0.title }}
          </slot>
        </div>
        <zm-radio-group
          class="opc-card-radio opc-card-cascade__group0-radios"
          aria-labelledby="group0-title"
          name="group0"
          :model-value="modelValue[0]"
          @change="handleGroup0Change"
        >
          <zm-tooltip v-for="(item, index) in billingCycleOptions" :key="index" :disabled="!(typeof item.disabled === 'string' && !!item.disabled)">
            <template #content>
              <div v-html="item.disabled || ''" />
            </template>
          <zm-radio :data-testid="`checkout-radio-billing-cycle-zp-${item.value}`" :label="item.value" :disabled="!!item.disabled">
            <div class="opc-card-cascade__group0-title first-capitalize">
              {{ item.title }}
              <!-- <save-count :monthly-price='computeOneMonthlyPrice(shortestCycleOption)'
                          :annual-price='computeOneMonthlyPrice(item) * 12' :disabled='!!item.disabled' /> -->
            </div>
            <div class="opc-card-cascade__group0-subtitle">
              <div class="opc-card-cascade__group0-subtitle-price">
                {{ item.subTitle }}
              </div>
              <span v-html="item.description"></span><priceVATTip />
            </div>
          </zm-radio>
          </zm-tooltip>
        </zm-radio-group>
        <zm-banner
          v-if="monthlyDisabledAlertMessage"
          type="warning"
          style="margin: 14px 0px 0px;"
        >
          <p v-html="monthlyDisabledAlertMessage" />
        </zm-banner>
      </div>
      <div v-if="divideLine" class="divide-line"></div>
    </template>
    <div class="opc-card-cascade__item" v-if="callingFeeSwitchVisible">
      <div class="opc-card-cascade__title">
        <slot :name="group1.slot">
          <span id="group1-title">{{ group1.title }}</span>
        </slot>
      </div>
      <zm-radio-group
        class="opc-card-radio opc-card-cascade__group1-radios"
        aria-labelledby="group1-title"
        name="group1"
        :model-value="modelValue[1]"
        @change="handleGroup1Change"
      >
        <zm-radio v-for="item in group1.options" :data-testid="`checkout-radio-billing-cycle-${item.value}`" :key="item.value" :label="item.value" :aria-label="group1.ariaLabel">
          <p class="opc-card-cascade__group1-title">{{ item.title }}</p>
          <div class="opc-card-cascade__group1-subtitle">
            <span class="opc-card-cascade__group1-subtitle-desc">{{ item.subTitle }}</span>
            <span v-html="item.description"></span><priceVATTip />
          </div>
        </zm-radio>
      </zm-radio-group>
    </div>
  </div>
</template>

<script>
// import SaveCount from '@/components/save-count.vue';
import _ from 'lodash';
import { BillingCycleEnum } from '@/common/constants';

export default {
  name: 'OpcCardCascade',
  // components: { SaveCount },
  data: () => {
    return {};
  },
  props: {
    monthlyDisabledAlertMessage: String,
    trackingData: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => {},
    },
    divideLine: Boolean,
    billingCycleSwitchVisible: {
      type: Boolean,
      default: true,
    },
    callingFeeVisible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    group0() {
      return {
        ...this.config.group0,
        options: this.config.options,
      };
    },
    group1() {
      return this.getGroup1(this.modelValue[0]);
    },
    shortestCycleOption() {
      return _.sortBy(this.group0.options, o => BillingCycleEnum[o.value])[0];
    },
    callingFeeSwitchVisible() {
      if (!this.callingFeeVisible) return false;
      return this.group1.options.length;
    },
    billingCycleOptions() {
      return _.reverse(this.group0.options);
    },
  },
  methods: {
    emitValue(v) {
      this.$emit('update:modelValue', v);
      this.$emit('change', v);
    },
    handleGroup0Change(v) {
      const group1Opts = this.getGroup1(v).options
      if (!group1Opts.find((item) => item.value === this.modelValue[1])) {
        const group1Value = group1Opts.at(-1).value;
        this.emitValue([v, group1Value]);
      } else {
        this.emitValue([v, this.modelValue[1]]);
      }
      const { buyInteraction, getBuySelection } = this.trackingData[0] || {}
      this.$tracking.buyInteraction(buyInteraction, (getBuySelection && getBuySelection(v)) || v || '')
    },
    handleGroup1Change(v) {
      this.emitValue([this.modelValue[0], v]);

      const { buyInteraction, getBuySelection } = this.trackingData[1] || {}
      this.$tracking.buyInteraction(buyInteraction, (getBuySelection && getBuySelection(this.modelValue[0], v)) || v || '')
    },
    computeOneMonthlyPrice(option) {
      return option[option.value] / BillingCycleEnum[option.value];
    },
    getGroup1(v) {
      const group1Options = this.config.options.find((item) => item.value === v)?.options ?? [];
      return {
        ...this.config.group1,
        options: group1Options,
      };
    }
  }
};
</script>

<style lang="scss">
/**
 * let the "following arbitrary element" add top margin itself,
 * which is equivalent to "add 16px if there is a sibling,
 * add 0px if there is no sibling", and will not affect .opc-card-cascade itself.
 */
.opc-card-cascade + * {
  margin-top: 16px;
}

.opc-card-cascade {
  .opc-card-cascade__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;

    &:nth-last-of-type(1) {
      margin-bottom: 0;
    }
  }

  .is-disabled {
    .opc-card-cascade__group0-title,
    .opc-card-cascade__group0-subtitle,
    .opc-card-cascade__group0-subtitle-price,
    .opc-card-cascade__group1-title,
    .opc-card-cascade__group1-subtitle,
    .opc-card-cascade__group1-subtitle-price {
      color: var(--zoom-color-state-disable, #ADB1B8);
    }
  }

  &__group0-title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 4px;
  }
  &__group0-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0em;
    color: var(--zoom-color-text-strong-neutral, #555B62);
    &-price {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 28px;
    }
    &-desc {
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
      letter-spacing: 0em;
      text-align: center;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }
  &__group1-title {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    margin-bottom: 4px;
  }
  &__group1-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0em;
    color: var(--zoom-color-text-strong-neutral, #555B62);
    text-align: left;
    &-desc {
      font-size: 24px;
      font-weight: 600;
      line-height: 28px;
      letter-spacing: 0em;
      text-align: left;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }

  .opc-card-cascade__title {
    display: flex;
    align-items: center;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 8px;
    .zbo-popover__content {
      font-size: 14px;
      font-weight: 400;
    }
  }

  .divide-line {
    width: 100%;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
    margin: 16px 0;
  }
}
</style>
